import { useState } from "react";
import { ScheduleItem, STATUS_CONFIG, TYPE_CONFIG } from "../types";

interface Props {
  item: ScheduleItem;
  onClose: () => void;
  onEditTime: (item: ScheduleItem) => void;
  onCancel: (item: ScheduleItem) => void;
  onRestore: (item: ScheduleItem) => void;
}

export default function ScheduleDetailModal({ item, onClose, onEditTime, onCancel, onRestore }: Props) {
  const [recipientFilter, setRecipientFilter] = useState<"all" | "page">("all");
  const [recipientPage, setRecipientPage] = useState(1);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const statusCfg = STATUS_CONFIG[item.status];
  const typeCfg = TYPE_CONFIG[item.type];

  const RECIPIENT_PAGE_SIZE = 8;
  const totalRecipientPages = Math.ceil(item.recipients.length / RECIPIENT_PAGE_SIZE);
  const pagedRecipients = item.recipients.slice(
    (recipientPage - 1) * RECIPIENT_PAGE_SIZE,
    recipientPage * RECIPIENT_PAGE_SIZE
  );

  const handleCancel = () => {
    setShowCancelConfirm(false);
    onCancel(item);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#13131f] border border-white/[0.1] rounded-t-2xl md:rounded-2xl w-full md:max-w-2xl max-h-[90vh] md:max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-white/[0.07] shrink-0">
          <div className="flex items-center gap-2 md:gap-3">
            <span className={`inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded text-[10px] md:text-xs font-semibold ${typeCfg.className}`}>
              {item.type}
            </span>
            <div>
              <h3 className="text-white text-xs md:text-sm font-semibold">
                {item.subject ?? "예약 발송 상세"}
              </h3>
              <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">등록일: {item.createdAt}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <span className={`inline-flex items-center gap-1 md:gap-1.5 px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${statusCfg.className}`}>
              <span className={`w-1 md:w-1.5 h-1 md:h-1.5 rounded-full ${statusCfg.dotClass}`}></span>
              {statusCfg.label}
            </span>
            <button
              onClick={onClose}
              className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xs md:text-sm"></i>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-5">
          {/* 요약 카드 */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <div className="px-3 py-2.5 md:px-4 md:py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <p className="text-gray-500 text-[10px] md:text-xs mb-0.5 md:mb-1">수신자 수</p>
              <p className="text-white text-base md:text-lg font-semibold">{item.recipients.length.toLocaleString()}<span className="text-gray-500 text-[10px] md:text-xs font-normal ml-0.5 md:ml-1">명</span></p>
            </div>
            <div className="px-3 py-2.5 md:px-4 md:py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <p className="text-gray-500 text-[10px] md:text-xs mb-0.5 md:mb-1">예약 시각</p>
              <p className="text-violet-400 text-xs md:text-sm font-semibold">{item.scheduledAt}</p>
            </div>
            <div className="px-3 py-2.5 md:px-4 md:py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <p className="text-gray-500 text-[10px] md:text-xs mb-0.5 md:mb-1">예상 비용</p>
              <p className="text-white text-base md:text-lg font-semibold">₩{item.cost.toLocaleString()}</p>
            </div>
          </div>

          {/* 발신 정보 */}
          <div className="grid grid-cols-2 gap-2 md:gap-3">
            <div className="flex items-center gap-2 md:gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg bg-white/[0.05]">
                <i className="ri-phone-line text-gray-400 text-xs md:text-sm"></i>
              </div>
              <div>
                <p className="text-gray-500 text-[10px] md:text-xs">발신번호</p>
                <p className="text-gray-200 text-xs md:text-sm font-medium">{item.senderNumber}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg bg-white/[0.05]">
                <i className="ri-calendar-schedule-line text-gray-400 text-xs md:text-sm"></i>
              </div>
              <div>
                <p className="text-gray-500 text-[10px] md:text-xs">등록 시각</p>
                <p className="text-gray-200 text-xs md:text-sm font-medium">{item.createdAt}</p>
              </div>
            </div>
          </div>

          {/* 메시지 내용 */}
          <div>
            <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">메시지 내용</p>
            <div className="px-3 py-3 md:px-4 md:py-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              {item.subject && (
                <p className="text-white text-xs md:text-sm font-semibold mb-1.5 md:mb-2 pb-1.5 md:pb-2 border-b border-white/[0.06]">
                  {item.subject}
                </p>
              )}
              <p className="text-gray-300 text-xs md:text-sm leading-relaxed whitespace-pre-wrap">{item.message}</p>
              {item.hasImage && (
                <div className="mt-2 md:mt-3 flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-image-line text-amber-400 text-[10px] md:text-xs"></i>
                  </div>
                  <span className="text-amber-400 text-[10px] md:text-xs">이미지 첨부 포함</span>
                </div>
              )}
            </div>
          </div>

          {/* 수신자 목록 */}
          <div>
            <div className="flex items-center justify-between mb-1.5 md:mb-2">
              <p className="text-gray-400 text-[10px] md:text-xs font-medium">
                수신자 목록 <span className="text-gray-600">({item.recipients.length}명)</span>
              </p>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
              <div className="divide-y divide-white/[0.04]">
                {pagedRecipients.map((r, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 md:px-4 py-2 md:py-2.5">
                    <div className="flex items-center gap-2 md:gap-2.5">
                      <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full bg-white/[0.06] text-gray-400 text-[10px] md:text-xs font-medium">
                        {r.name ? r.name.charAt(0) : "#"}
                      </div>
                      <span className="text-gray-300 text-[10px] md:text-xs">{r.name ?? "이름 없음"}</span>
                    </div>
                    <span className="text-gray-500 text-[10px] md:text-xs font-mono">{r.phone}</span>
                  </div>
                ))}
              </div>
              {totalRecipientPages > 1 && (
                <div className="flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 border-t border-white/[0.06]">
                  <button
                    onClick={() => setRecipientPage((p) => Math.max(1, p - 1))}
                    disabled={recipientPage === 1}
                    className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-left-s-line text-xs md:text-sm"></i>
                  </button>
                  <span className="text-gray-500 text-[10px] md:text-xs">{recipientPage} / {totalRecipientPages}</span>
                  <button
                    onClick={() => setRecipientPage((p) => Math.min(totalRecipientPages, p + 1))}
                    disabled={recipientPage === totalRecipientPages}
                    className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-xs md:text-sm"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-white/[0.07] shrink-0">
          {showCancelConfirm ? (
            <div className="space-y-2.5 md:space-y-3">
              <div className="flex items-start gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl bg-red-500/10 border border-red-500/20">
                <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-error-warning-line text-red-400 text-xs md:text-sm"></i>
                </div>
                <div>
                  <p className="text-red-300 text-xs md:text-sm font-medium">예약을 취소하시겠습니까?</p>
                  <p className="text-red-400/70 text-[10px] md:text-xs mt-0.5">취소 후에는 재예약을 통해 다시 설정할 수 있습니다.</p>
                </div>
              </div>
              <div className="flex gap-2 md:gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 text-xs md:text-sm hover:text-white cursor-pointer transition-colors whitespace-nowrap"
                >
                  돌아가기
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-red-600/80 text-white text-xs md:text-sm font-medium hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  예약 취소 확인
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 md:gap-3">
              {item.status === "scheduled" && (
                <>
                  <button
                    onClick={() => { onClose(); onEditTime(item); }}
                    className="flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-300 text-xs md:text-sm hover:text-white hover:bg-white/[0.08] cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-time-line text-xs md:text-sm"></i>
                    시간 수정
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs md:text-sm hover:bg-red-500/20 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-calendar-close-line text-xs md:text-sm"></i>
                    예약 취소
                  </button>
                </>
              )}
              {item.status === "cancelled" && (
                <button
                  onClick={() => { onClose(); onRestore(item); }}
                  className="flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs md:text-sm hover:bg-violet-500/30 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-xs md:text-sm"></i>
                  재예약
                </button>
              )}
              <button
                onClick={onClose}
                className="ml-auto px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 text-xs md:text-sm hover:text-white cursor-pointer transition-colors whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}